import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import logo from '../libs/assets/images/logo1.svg';

const Header = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const { user, logout } = useAuth();
    const userMenuRef = useRef(null);

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        if (showUserMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserMenu]);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const getUserDisplayName = () => {
        if (user?.attributes?.email) {
            return user.attributes.email;
        }
        if (user?.username) {
            return user.username;
        }
        return 'User';
    };

    const getUserInitials = () => {
        const displayName = getUserDisplayName();
        return displayName.substring(0, 2).toUpperCase();
    };

    return (
        <div className="bg-gray-900 pb-32">
            <nav className="bg-gray-900">
                <div className="mx-auto max-w sm:px-6 lg:px-8">
                    <div className="">
                        <div className="flex h-16 items-center justify-between pt-2 px-4 sm:px-0">
                            <div className="flex items-center">
                                <div className="shrink-0">
                                    <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
                                        <img src={logo} alt="SailPoint Logo" className="h-24 w-auto" />
                                    </a>
                                </div>
                            </div>
                            
                            {/* Desktop user menu */}
                            <div className="hidden md:block">
                                <div className="ml-4 flex items-center md:ml-6">
                                    <div className="relative" ref={userMenuRef}>
                                        <button
                                            type="button"
                                            className="relative flex max-w-xs items-center rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                                            onClick={() => setShowUserMenu(!showUserMenu)}
                                            aria-expanded={showUserMenu}
                                            aria-haspopup="true"
                                        >
                                            <span className="sr-only">Open user menu</span>
                                            <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
                                                {getUserInitials()}
                                            </div>
                                        </button>
                                        
                                        {showUserMenu && (
                                            <div
                                                className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
                                                role="menu"
                                                aria-orientation="vertical"
                                            >
                                                <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                                                    <p className="font-medium truncate">{getUserDisplayName()}</p>
                                                </div>
                                                <button
                                                    onClick={handleLogout}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                >
                                                    Sign out
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Mobile menu button */}
                            <div className="-mr-2 flex md:hidden">
                                <button
                                    type="button"
                                    className="relative inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden"
                                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                    aria-expanded={isMobileMenuOpen}
                                    aria-label="Open main menu"
                                >
                                    <span className="absolute -inset-0.5"></span>
                                    <span className="sr-only">Open main menu</span>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="size-6">
                                        <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile menu */}
                    {isMobileMenuOpen && (
                        <div className="md:hidden">
                            <div className="border-t border-gray-700 pb-3 pt-4">
                                <div className="flex items-center px-5">
                                    <div className="shrink-0">
                                        <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
                                            {getUserInitials()}
                                        </div>
                                    </div>
                                    <div className="ml-3">
                                        <div className="text-base font-medium text-white">{getUserDisplayName()}</div>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1 px-2">
                                    <button
                                        onClick={handleLogout}
                                        className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white w-full text-left"
                                    >
                                        Sign out
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </nav>
            <header className="py-10">
                <div className="mx-auto px-4 sm:px-6 lg:px-24">
                    <h1 className="text-3xl font tracking-tight text-white">
                        Benefits Analysis
                    </h1>
                </div>
            </header>
        </div>
    );
};

export default Header;
